<template>
    <div class="expansion-panel flex flex-column" :id="value">
        <component :is="tag" :to="to" class="flex justify-between" :class="[headerPadding, padding, {'cursor-pointer': $slots.default}]" @click="handleClick" role="button">
            <div class="flex flex-column w-full">
                <div class="flex items-center flex-grow-1">
                    <component v-if="title" :is="titleTag" class="text-heading-color font-heading leading-tight mb-0" :class="[isActive ? 'font-bold' : 'font-regular', titleTextSizeString]" v-html="title"/>
                    <slot name="header"/>
                </div>
                <span v-if="subtitle" class="text-gray-400 leading-tight text-xs mt-4">{{ subtitle }}</span>
            </div>
            <div class="flex">
                <btn v-if="$slots.default || to" x-small icon>
                    <Icon :icon="dropIcon" force-size/>
                </btn>

                <slot name="header_suffix"/>
            </div>
        </component>
        <expand-transition v-if="$slots.default" @leave="isClosing = true" @after-leave="isClosing = false">
            <div v-show="isActive" :aria-expanded="isActive">
                <div :class="[contentPadding]">
                    <div v-if="description" :class="padding">
                        <p class="subheading-description">{{ description }}</p>
                    </div>

                    <slot/>
                </div>
            </div>
        </expand-transition>
    </div>
</template>
<script>
import {inject as RegistrableInject} from "../../mixins/Registrable";
    import Toggleable from "../../mixins/Toggleable";
    import {mdiChevronDown, mdiChevronRight} from "@mdi/js";
    import Sizeable from "../../mixins/Sizeable";
	import {expansionPanelInjection} from "../../types/keys";

    export default {
        name: 'ExpansionPanel',
        mixins: [RegistrableInject(expansionPanelInjection), Toggleable, Sizeable],

        props: {

            // The unique value to identify the expansion item
            // Defaults to the DOM hierarchy index of the parent
            value: {
                type: [String, Number],
                default: null
            },

            // The title to display on the header
            title: {
                type: String,
                default: null
            },

            // The subtitle to display on the header below the title
            subtitle: {
                type: String,
                default: null
            },

            // The description text to display on the top of the opened content
            description: {
                type: String,
                default: null
            },

            // A custom padding class to apply to the header
            padding: {
                type: String,
                default: null
            },

			// A link to redirect to when clicked on the panel
			to: {
				type: [String, Object],
				default: null
			},

			titleTag: {
				type: String,
				default: 'span'
			}
        },

        data() {
            return {
                isClosing: false
            }
        },

        computed: {
			tag() {
				if (this.to) {
					return resolveComponent('NuxtLink');
				}

				return 'div';
			},

            dropIcon() {
                return this.isActive ? mdiChevronDown : mdiChevronRight;
            },

            headerPadding() {
                switch (this.sizeString) {
                    case '2xs':
                        return 'py-2'
                    case 'xs':
                        return 'py-4'
                    case 'sm':
                        return 'py-8' // Fixed
                    case 'md':
                        return (this.isClosing || this.isActive) && this.description ? 'pt-12 pb-4' : 'py-12' // Fixed
                    case 'lg':
                        return 'py-14'
                    case 'xl':
                        return 'py-16'
                    case '2xl':
                        return 'py-18'
                }

                return (this.isClosing || this.isActive) && this.description ? 'pt-12 pb-4' : 'py-12' // Fixed
            },

            contentPadding() {
                switch (this.sizeString) {
                    case '2xs':
                        return 'pb-2' + (this.description ? '' : ' pt-0')
                    case 'xs':
                        return 'pb-4' + (this.description ? '' : ' pt-2')
                    case 'sm':
                        return 'pb-8' + (this.description ? '' : ' pt-4') // Fixed
                    case 'md':
                        return 'pb-16' + (this.description ? '' : ' pt-8') // Fixed
                    case 'lg':
                        return 'pb-20' + (this.description ? '' : ' pt-10')
                    case 'xl':
                        return 'pb-24' + (this.description ? '' : ' pt-12')
                    case '2xl':
                        return 'pb-28' + (this.description ? '' : ' pt-14')
                }

                return 'pb-16' + (this.description ? '' : ' pt-8'); // Fixed
            },

            titleTextSizeString() {
                if (this.xxSmall || this.size === '2xs') {
                    return 'text-2xs';
                }

                if (this.xSmall || this.size === 'xs') {
                    return 'text-xs';
                }

                if (this.small || this.size === 'sm') {
                    return 'text-sm';
                }

                if (this.medium || this.size === 'md') {
                    return 'text-sub';
                }

                if (this.large || this.size === 'lg') {
                    return 'text-sub sm:text-md md:text-lg';
                }

                if (this.xLarge || this.size === 'xl') {
                    return 'text-sub sm:text-lg md:text-xl';
                }

                if (this.xxLarge || this.size === '2xl') {
                    return 'text-sub sm:text-lg md:text-xl lg:text-2xl';
                }

                return 'text-sub';
            }
        },

        created() {
            this.injected && this.injected.register(this)
        },

        beforeUnmount() {
            this.injected && this.injected.unregister(this)
        },

		methods: {
			handleClick() {
				if (!this.to) {
					this.isActive = !this.isActive;
				}
			}
		}
    }
</script>
<style>
/*********************
  Inputs
**********************/
.expansion-panel:not(:last-child) {
  border-bottom: 1px solid #e6ecf5;
}
</style>